import { useState } from "react";
import { api, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, RefreshCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

const tone = (r) => (r === "ok" || r === "200 test" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-rose-100 text-rose-700 border-rose-200");

const AttemptRow = ({ a }) => (
  <tr className="border-t border-slate-100 align-top" data-testid={`wp-attempt-${a.id}`}>
    <td className="px-2 py-2 whitespace-nowrap">{fmtDateTime(a.at)}</td>
    <td className="px-2 py-2 font-mono">{a.ip}</td>
    <td className="px-2 py-2"><Badge className={tone(a.result)}>{a.result}</Badge><div className="text-slate-500 mt-1 max-w-[260px]">{a.detail}</div></td>
    <td className="px-2 py-2">{a.http_status}</td>
    <td className="px-2 py-2"><div>{a.content_type || "—"}</div><div className="text-slate-500">{a.body_format} · {a.body_length} B</div><div className="text-slate-400 truncate max-w-[200px]" title={a.user_agent}>{a.user_agent}</div></td>
    <td className="px-2 py-2 font-mono">
      {(a.signature_headers || []).map((s) => <div key={s.name}>{s.name}: {s.format}, {s.length} Z.{s.prefix ? `, „${s.prefix}…“` : ""}</div>)}
      {(a.timestamp_headers || []).map((t) => <div key={t.name} className="text-slate-500">{t.name}={t.value}</div>)}
      {!a.signature_headers?.length && <span className="text-slate-400">kein Signatur-Header</span>}
      <div className="text-slate-500 mt-1">Variante: {a.variant}</div>
    </td>
    <td className="px-2 py-2 font-mono max-w-[220px]">
      <div>{(a.field_names || []).join(", ") || "—"}</div>
      {a.file_fields?.length > 0 && <div className="text-slate-500">Dateien: {a.file_fields.join(", ")}</div>}
      <details className="text-slate-400 mt-1"><summary className="cursor-pointer">Header ({a.header_names?.length || 0})</summary>{(a.header_names || []).join(", ")}</details>
    </td>
  </tr>
);

export const WebhookAttemptsSection = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [hidden, setHidden] = useState(false);
  const load = async () => {
    try { const r = await api.get("/integrationen/wordpress/webhook-versuche"); setItems(r.data); }
    catch (e) { if (e?.response?.status === 403) setHidden(true); else toast.error("Webhook-Verlauf konnte nicht geladen werden."); }
  };
  const toggle = () => { if (!open && items === null) load(); setOpen(!open); };
  const clear = async () => {
    if (!window.confirm("Webhook-Verlauf leeren?")) return;
    await api.delete("/integrationen/wordpress/webhook-versuche"); setItems([]); toast.success("Verlauf geleert.");
  };
  if (hidden) return null;
  return (
    <div className="border border-slate-200 rounded-md" data-testid="wp-attempts-section">
      <button type="button" onClick={toggle} className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700" data-testid="wp-attempts-toggle">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Technische Diagnose (Header, beide Endpunkte)
        {items && <span className="text-xs text-slate-400">({items.length})</span>}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load} data-testid="wp-attempts-refresh"><RefreshCcw className="w-3.5 h-3.5 mr-1" /> Aktualisieren</Button>
            <Button size="sm" variant="outline" onClick={clear} data-testid="wp-attempts-clear"><Trash2 className="w-3.5 h-3.5 mr-1" /> Verlauf leeren</Button>
          </div>
          {items?.length === 0 && <p className="text-xs text-slate-500" data-testid="wp-attempts-empty">Noch keine Webhook-Versuche protokolliert.</p>}
          {items?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="wp-attempts-table">
                <thead className="text-left text-slate-500"><tr>
                  <th className="px-2 py-1">Zeit</th><th className="px-2 py-1">IP</th><th className="px-2 py-1">Ergebnis</th><th className="px-2 py-1">HTTP</th>
                  <th className="px-2 py-1">Content-Type / Body</th><th className="px-2 py-1">Signatur</th><th className="px-2 py-1">Felder</th>
                </tr></thead>
                <tbody>{items.map((a) => <AttemptRow key={a.id} a={a} />)}</tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-slate-400">Es werden weder Secret, vollständige Signatur noch Feldinhalte gespeichert – nur Namen, Formate und Längen.</p>
        </div>
      )}
    </div>
  );
};
